import { appColors, CareerCategory } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  CareerPathWithScore,
  CareerUserResponseAnswer,
} from '@backrs_ui/grow-ui/src/models/models';

import { Box, Typography, SxProps, Theme } from '@mui/material';

import { CareerCategoryGroup } from './CareerCategoryGroup';
import { ResultsPill } from './ResultsPill';
import { RESULT_PAGE_TOUR_IDS } from '../tour/ResultPage.tour';
import { getCareerPathFitColor } from '../util/results.util';

const SUGGESTED_CAREER_PATH_STRINGS = appStrings.modules.results;

type SuggestedCareerPathsProps = Readonly<{
  careerPathSuggestions: CareerPathWithScore[];
  careerPathCategories: CareerCategory[];
  userResponses: CareerUserResponseAnswer[];
  setCurrentDialogCareerPath: (careerPath: CareerPathWithScore) => void;
}>;

export const SuggestedCareerPaths = ({
  careerPathSuggestions,
  careerPathCategories,
  setCurrentDialogCareerPath,
}: SuggestedCareerPathsProps) => {
  return (
    <Box sx={suggestedCareerPathsContainerStyle} id={RESULT_PAGE_TOUR_IDS.step4}>
      <Box sx={chipAndCircleContainerStyle}>
        <Box sx={pillAndDescriptionContainer}>
          <Box minWidth={'fit-content'}>
            <ResultsPill title={SUGGESTED_CAREER_PATH_STRINGS.suggestedCareerPaths} />
          </Box>
          <Typography variant="body1" sx={sectionDescriptionStyle}>
            {SUGGESTED_CAREER_PATH_STRINGS.sectionDescriptions.suggested}
          </Typography>
        </Box>
        <Box sx={circleGroupContainerStyle}>
          <Box sx={circleItemContainerStyle}>
            <Box sx={circleStyle} bgcolor={getCareerPathFitColor('TOP')} />
            <Typography variant="body1" lineHeight="1.25">
              {SUGGESTED_CAREER_PATH_STRINGS.suggestedFits.strong}
            </Typography>
          </Box>
          <Box sx={circleItemContainerStyle}>
            <Box sx={circleStyle} bgcolor={getCareerPathFitColor('MIDDLE')} />
            <Typography variant="body1" lineHeight="1.25">
              {SUGGESTED_CAREER_PATH_STRINGS.suggestedFits.medium}
            </Typography>
          </Box>
          <Box sx={circleItemContainerStyle}>
            <Box sx={circleStyle} bgcolor={getCareerPathFitColor('LOW')} />
            <Typography variant="body1" lineHeight="1.25">
              {SUGGESTED_CAREER_PATH_STRINGS.suggestedFits.low}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={columnsContainerStyle}>
        {careerPathCategories.map((category, index) => (
          <CareerCategoryGroup
            key={index}
            index={index}
            careerPathSuggestions={careerPathSuggestions}
            category={category}
            setCurrentDialogCareerPath={setCurrentDialogCareerPath}
          />
        ))}
      </Box>
    </Box>
  );
};

const pillAndDescriptionContainer: SxProps<Theme> = {
  display: 'flex',
  flexDirection: {
    xs: 'column',
    sm: 'column',
    md: 'column',
    lg: 'row',
  },
  justifyContent: {
    xs: 'space-between',
    md: 'space-between',
  },
  gap: 2,
  maxWidth: {
    xs: '100%',
    sm: '100%',
    md: '70%',
    lg: '80%',
  },
};

const suggestedCareerPathsContainerStyle: SxProps<Theme> = {
  backgroundColor: appColors.neutral.white,
  border: `2px solid ${appColors.neutral.black}`,
  borderRadius: '.5rem',
  boxSizing: 'border-box',
  paddingLeft: {
    xs: '1rem',
    sm: '2rem',
  },
  paddingRight: {
    xs: '.5rem',
    sm: '2rem',
  },
  paddingTop: '2rem',
  paddingBottom: '2rem',
  width: {
    xs: '100%',
    sm: '100%',
    md: '100%',
    lg: '65%',
  },
};

const sectionDescriptionStyle: SxProps<Theme> = {
  fontStyle: 'italic',
  fontSize: '14px',
  lineHeight: '1.25',
  mb: { xs: 2, sm: 2, md: 0 },
  width: {
    xs: '100%',
    sm: '100%',
    md: '100%',
    lg: '100%',
  },
};

const chipAndCircleContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: {
    xs: 'column',
    sm: 'column',
    md: 'row',
  },
  justifyContent: 'space-between',
};

const circleGroupContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: {
    xs: 'row',
    sm: 'column',
  },
  alignItems: 'flex-start',
  gap: 1,
};

const circleItemContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 1,
};

const circleStyle: SxProps<Theme> = {
  borderRadius: '100%',
  maxWidth: '24px',
  minWidth: '24px',
  minHeight: '24px',
  maxHeight: '24px',
};

const columnsContainerStyle: SxProps<Theme> = {
  flexDirection: 'column',
  display: 'flex',
  overflow: 'auto',
  flexWrap: 'wrap',
  maxHeight: 900,
  '@media (max-width: 1600px)': {
    maxHeight: 900,
  },
  '@media (max-width: 1350px)': {
    maxHeight: 1500,
  },
  '@media (max-width: 1200px)': {
    maxHeight: 1500,
  },
  '@media (max-width: 1033px)': {
    maxHeight: 'unset',
  },
  '@media (max-width: 900px)': {
    maxHeight: 900,
  },
  '@media (max-width: 825px)': {
    maxHeight: 1300,
  },
  '@media (max-width: 700px)': {
    maxHeight: 'unset',
  },
};
